import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCart } from "@/features/cart";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import {
  DeliveryAddressSection,
  BillingAddressSection,
  PaymentMethodSection,
  OrderSummarySection,
  CheckoutAgreementsSection,
  DeleteAddressDialog,
  PaymentMethodModal,
  DeletePaymentMethodDialog,
  InstallmentOptions,
  Checkout3DSModal,
  useCheckoutStore,
} from "@/features/checkout";
import { OrderProcessingScreen } from "@/features/checkout/components/OrderProcessingScreen";
import { useCheckoutCardInfo } from "@/features/checkout/hooks/use-card-info";
import { BackButton } from "@/components/ui/back-button";
import { AgreementViewModal } from "@/features/agreements";
import { CheckoutStepper } from "@/features/checkout/components/CheckoutStepper";
import { useLeftSidebar } from "@/components/ui/sidebar";

const CheckoutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isEmpty, isLoading } = useCart();
  const { error } = useToast();
  const { setBuyNow } = useCheckoutStore();
  const { open: isSidebarOpen, setOpen: setSidebarOpen } = useLeftSidebar();
  const previousSidebarOpenRef = React.useRef<boolean | null>(null);

  // URL'den hemen al parametrelerini oku
  const offerId = searchParams.get("offerId") || null;
  const quantity = offerId ? Number(searchParams.get("quantity") || 1) : 1;

  // Seçili kart değiştiğinde card info API'sine request at
  useCheckoutCardInfo();

  // offerId varsa checkout store'a set et, yoksa temizle
  // Bu sayede OrderSummarySection global cart'ı değil buy-now verisini gösterir
  // Header ve diğer bileşenler bu durumdan etkilenmez
  React.useEffect(() => {
    setBuyNow(offerId, quantity);
    return () => {
      // Checkout'tan çıkıldığında buy-now modunu temizle
      setBuyNow(null, 1);
    };
  }, [offerId, quantity, setBuyNow]);

  React.useEffect(() => {
    previousSidebarOpenRef.current = isSidebarOpen;
    if (isSidebarOpen) {
      setSidebarOpen(false);
    }

    return () => {
      if (previousSidebarOpenRef.current) {
        setSidebarOpen(true);
      }
    };
    // Checkout'a girince sidebar'ı bir kez kapat, çıkınca eski haline döndür
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // Buy-now modunda boş sepet kontrolü atla (TanStack Query kendi yüklemesini yapıyor)
    if (offerId) return;
    // Cart yükleniyorsa bekle, yüklendikten sonra kontrol et
    if (!isLoading && isEmpty) {
      error(t("checkout.emptyCartMessage"), {
        title: t("checkout.emptyCartTitle"),
        duration: 5000,
      });
      navigate("/", { replace: true });
    }
  }, [isEmpty, isLoading, navigate, error, t, offerId]);

  // Cart yükleniyorsa loading göster
  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-4 md:py-8 px-4 md:px-2">
        <div className="text-center mb-4 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
            {t("checkout.title")}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground">
            {t("checkout.subtitle")}
          </p>
        </div>
        <div className="flex justify-center items-center py-16">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-bg-primary dark:bg-bg-primary">
      {/* Header Section - Consistent with other pages */}
      <div className="w-full border-b border-border-secondary dark:border-border-secondary">
        <div className="pt-4 md:pt-6 pb-4 md:pb-6 max-w-screen-2xl px-4 md:px-8 mx-auto">
          <div className="flex flex-col gap-2 md:gap-4">
            {/* Title & Description */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <BackButton />
                <h1 className="text-lg md:text-xl lg:text-2xl font-semibold text-text-primary dark:text-text-primary leading-6 md:leading-7 lg:leading-8">
                  {t("checkout.title")}
                </h1>
              </div>
              <p className="ml-8 text-xs md:text-sm lg:text-base text-text-tertiary dark:text-text-tertiary leading-4 md:leading-5 lg:leading-6">
                {t("checkout.subtitle")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Consistent padding */}
      <div className="px-4 md:px-16 py-4 md:py-6">
        <div className="max-w-screen-2xl mx-auto w-full">
          <CheckoutStepper />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_450px] gap-4 md:gap-6 lg:gap-8">
            {/* Left side: Forms (flexible width) */}
            <div className="min-w-0 space-y-4 md:space-y-6">
              <DeliveryAddressSection />
              <BillingAddressSection />
              <PaymentMethodSection />
              <InstallmentOptions />
              <CheckoutAgreementsSection />
            </div>

            {/* Right side: Order Summary (450px fixed width, sticky) */}
            <div className="min-w-0">
              <div className="lg:sticky lg:top-32">
                <OrderSummarySection />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs - Zero Props */}
      <DeleteAddressDialog />
      <PaymentMethodModal />
      <DeletePaymentMethodDialog />
      <Checkout3DSModal />
      <AgreementViewModal />

      {/* PostMessage Listener for Checkout Results */}
      <CheckoutResultListener />

      {/* Order Processing Screen - tam ekran, polling ile durumu takip eder */}
      <OrderProcessingScreen />
    </div>
  );
};

/**
 * PostMessage listener component for checkout results
 * Handles success/failure events from 3DS modal
 */
const CheckoutResultListener: React.FC = () => {
  const { t } = useTranslation();
  const { error } = useToast();
  const { close3DSModal, openProcessingScreen } = useCheckoutStore();

  React.useEffect(() => {
    const handleCheckoutResult = (event: MessageEvent) => {
      if (event.data?.type === "checkout_result") {
        const { result, orderGroupId, message } = event.data;

        if (result === "success" && orderGroupId) {
          // 3DS tamamlandı → processing screen aç (polling başlar)
          close3DSModal();
          openProcessingScreen(orderGroupId);
        } else {
          const errorMessage = message || t("checkout.paymentFailed");
          error(errorMessage, { duration: 5000 });
        }
      }
    };

    window.addEventListener("message", handleCheckoutResult);
    return () => window.removeEventListener("message", handleCheckoutResult);
  }, [error, t, close3DSModal, openProcessingScreen]);

  return null;
};

export default CheckoutPage;
